import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService, creditNoteService } from '../../../api/apiService';
import { sendCreditNoteEmails } from './creditNoteEmail';
import MultiSelectFilter from './MultiSelectFilter';
import { STATUS_CREDIT_NOTE_PENDING } from '../../../helpers/bookingStatus';
import confirmIcon from '../../../images/confirm.svg';
import '../../../App.css';
import './ConfirmedListCreditNotes.css';

/**
 * The credit note queue: bookings whose talent has been confirmed and notified, and whose
 * Gutschrift is still outstanding (STATUS_CREDIT_NOTE_PENDING — set when the booking is
 * confirmed under Confirmed Orders with "Credit note" ticked).
 *
 * Creating the document here is step 2 of the client's process: it happens AFTER the job so
 * the tasks can still be corrected, several bookings of one talent can be combined onto one
 * PDF, and the PDF is emailed to the talent at that moment. From then on the document is
 * fixed — corrections go through a Storno and a new number.
 */
function ConfirmedListCreditNotes() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createMessage, setCreateMessage] = useState(null);
  const [createErrors, setCreateErrors] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // "Generate the PDFs only after the jobs are completed" — on by default so a document
  // cannot be raised for a job that is still running and might still change.
  const [onlyFinished, setOnlyFinished] = useState(true);

  // Filters — all multi-select, an empty array means "no restriction". The queue is worked
  // through per talent/agency/week, so every filter below can be combined.
  const [filterTalents, setFilterTalents] = useState([]);
  const [filterAgencies, setFilterAgencies] = useState([]);
  const [filterClients, setFilterClients] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterWeeks, setFilterWeeks] = useState([]);
  const [filterSets, setFilterSets] = useState([]);
  const [filterLocations, setFilterLocations] = useState([]);
  const [filterFunctions, setFilterFunctions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bookingService.searchBookings({
        status: STATUS_CREDIT_NOTE_PENDING,
        limit: 500,
        offset: 0
      });
      const data = res.data;
      const list = data?.bookings || data || [];
      setBookings(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading confirmed bookings:', err);
      setError(err.message || 'Failed to load confirmed bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateCreditNote = async (booking) => {
    if (booking.credit_note_generated) return;
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings([booking.id]);
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`Credit note ${created[0].creditNoteNumber} created. Sending email…`);
        await load();
        const mail = await sendCreditNoteEmails(created);
        setCreateMessage(`Credit note ${created[0].creditNoteNumber} created. `
          + (mail.failed.length ? 'Email FAILED — see below.' : 'Email sent.'));
        if (mail.failed.length) setCreateErrors(mail.failed);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit note']);
    } finally {
      setCreateLoading(false);
    }
  };

  /** A job counts as finished once its last day is over. */
  const isFinished = (b) => {
    const raw = b?.end_date || b?.start_date;
    if (!raw) return false;
    const end = typeof raw === 'string' ? new Date(raw) : raw;
    if (isNaN(end.getTime())) return false;
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime() < Date.now();
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? d : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const clientName = (b) => {
    if (!b?.client) return '—';
    const c = typeof b.client === 'string' ? (b.client && b.client.startsWith('{') ? JSON.parse(b.client) : {}) : b.client;
    return c?.name || c?.label || (typeof b.client === 'string' ? b.client : '—');
  };

  // Always a string: sql_talent_id is a number, and the filters compare against option strings
  const talentDisplay = (b) => {
    const t = b.talent_name || b.talent_id || b.sql_talent_id;
    return t != null && String(t).trim() ? String(t).trim() : '—';
  };
  const statusDisplay = (b) => (b.status ? String(b.status) : '—');
  // Agency name from the talent master data; short/placeholder values mean "no agency"
  const agencyDisplay = (b) => {
    const a = b.talent_agency != null ? String(b.talent_agency).trim() : '';
    return a.length > 1 ? a : '—';
  };

  /** Set the job was shot on — "On Model", "On Produkt", "digital Production". */
  const setDisplay = (b) => {
    const s = b.set != null ? String(b.set).trim() : '';
    return s || '—';
  };

  /** Studio location; the backend sends it as an object with name/short. */
  const locationDisplay = (b) => {
    if (b.location && typeof b.location === 'object') return b.location.short || b.location.name || '—';
    if (typeof b.location === 'string' && b.location.trim()) return b.location.trim();
    return '—';
  };

  /**
   * The function booked (Foto, Styling, …). The booking's own position wins — it is what the
   * job was booked for — and the talent's master-data Funktion fills in for older bookings
   * that were created without one.
   */
  const functionDisplay = (b) => {
    const p = b.position;
    if (p && typeof p === 'object') {
      const v = p.label || p.value;
      if (v) return String(v).trim();
    } else if (p) {
      const s = String(p).trim();
      if (s) return s;
    }
    const f = b.talent_function != null ? String(b.talent_function).trim() : '';
    return f || '—';
  };

  /**
   * Everyone who touched the booking. Accounting asked to narrow the queue down to the
   * colleague who handled a job, and that person can sit in any of these fields depending on
   * how the booking was created.
   */
  const bookingUsers = (b) => {
    const raw = [b.created_by, b.proposed_by, b.proposedBy, b.processing_by, b.processing_by_email];
    return [...new Set(raw.map((u) => (u != null ? String(u).trim() : '')).filter(Boolean))];
  };

  /** ISO calendar week + ISO year of a date (the Thursday rule, as used across the app). */
  const isoWeek = (date) => {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3); // Thursday of this week decides week + year
    const isoYear = target.getFullYear();
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return { week: 1 + Math.ceil((firstThursday - target) / 604800000), year: isoYear };
  };

  /** "KW 33 / 2026" for the week the job started in — how the studio refers to a job week. */
  const weekDisplay = (b) => {
    const raw = b?.start_date || b?.end_date;
    if (!raw) return '';
    const d = typeof raw === 'string' ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return '';
    const { week, year } = isoWeek(d);
    return `KW ${String(week).padStart(2, '0')} / ${year}`;
  };

  /**
   * The job name in the same shape as the Lano task title
   * (KW33 _Client_Position_Talent_dd.mm.yyyy-dd.mm.yyyy_LocationShort), so accounting sees
   * the same string here as in Lano. The booking's own free-text title is confusing on its own
   * because it says nothing about client, week or location.
   */
  const lanoTitle = (b) => {
    const toDate = (raw) => {
      if (!raw) return null;
      const d = typeof raw === 'string' ? new Date(raw) : raw.seconds ? new Date(raw.seconds * 1000) : raw;
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    };
    const start = toDate(b.start_date);
    const end = toDate(b.end_date) || start;
    const kw = start ? `KW${isoWeek(start).week} ` : '';
    const client = clientName(b);
    const position = functionDisplay(b);
    const talent = talentDisplay(b);
    const loc = locationDisplay(b);
    const dates = start ? `${start.toLocaleDateString('de-DE')}-${end.toLocaleDateString('de-DE')}` : '';
    const part = (v) => (v && v !== '—' ? v : '');
    return `${kw}_${part(client)}_${part(position)}_${part(talent)}_${dates}_${part(loc)}`;
  };

  // Dropdown options — derived from the loaded queue, so only values that actually occur show up
  const optionsOf = (getter) => {
    const values = bookings.map((b) => {
      const v = getter(b);
      return v == null ? '' : String(v).trim();
    });
    const s = new Set(values.filter((v) => v && v !== '—'));
    return [...s].sort((a, b) => a.localeCompare(b));
  };

  const talentOptions = useMemo(() => optionsOf(talentDisplay), [bookings]);
  const agencyOptions = useMemo(() => optionsOf(agencyDisplay), [bookings]);
  const clientOptions = useMemo(() => optionsOf(clientName), [bookings]);
  const setOptions = useMemo(() => optionsOf(setDisplay), [bookings]);
  const locationOptions = useMemo(() => optionsOf(locationDisplay), [bookings]);
  const functionOptions = useMemo(() => optionsOf(functionDisplay), [bookings]);
  const userOptions = useMemo(() => {
    const s = new Set(bookings.flatMap(bookingUsers));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [bookings]);
  // Newest week first — the queue is worked backwards from the week that just ended. Sorted on
  // year+week, not on the label, so KW 05 / 2026 stays above KW 52 / 2025.
  const weekOptions = useMemo(() => {
    const s = new Set(bookings.map(weekDisplay).filter(Boolean));
    const sortKey = (label) => {
      const m = label.match(/KW (\d+) \/ (\d+)/);
      return m ? Number(m[2]) * 100 + Number(m[1]) : 0;
    };
    return [...s].sort((a, b) => sortKey(b) - sortKey(a));
  }, [bookings]);

  const activeFilterCount =
    filterTalents.length + filterAgencies.length + filterClients.length + filterUsers.length
    + filterWeeks.length + filterSets.length + filterLocations.length + filterFunctions.length;

  const matchesFilters = (b) => {
    if (filterTalents.length > 0 && !filterTalents.includes(talentDisplay(b))) return false;
    if (filterAgencies.length > 0 && !filterAgencies.includes(agencyDisplay(b))) return false;
    if (filterClients.length > 0 && !filterClients.includes(clientName(b))) return false;
    if (filterSets.length > 0 && !filterSets.includes(setDisplay(b))) return false;
    if (filterLocations.length > 0 && !filterLocations.includes(locationDisplay(b))) return false;
    if (filterFunctions.length > 0 && !filterFunctions.includes(functionDisplay(b))) return false;
    if (filterWeeks.length > 0 && !filterWeeks.includes(weekDisplay(b))) return false;
    if (filterUsers.length > 0 && !bookingUsers(b).some((u) => filterUsers.includes(u))) return false;
    return true;
  };

  const resetFilters = () => {
    setFilterTalents([]);
    setFilterAgencies([]);
    setFilterClients([]);
    setFilterUsers([]);
    setFilterWeeks([]);
    setFilterSets([]);
    setFilterLocations([]);
    setFilterFunctions([]);
  };

  // Everything the buttons below act on. Hiding running or filtered-out jobs must also keep
  // them out of "create for selected" — otherwise a filter would be cosmetic only.
  const visible = useMemo(
    () => (onlyFinished ? bookings.filter(isFinished) : bookings).filter(matchesFilters),
    [bookings, onlyFinished, filterTalents, filterAgencies, filterClients, filterUsers,
      filterWeeks, filterSets, filterLocations, filterFunctions]
  );
  const runningCount = bookings.length - bookings.filter(isFinished).length;

  // A booking that a filter just hid must not stay selected — a credit note would otherwise be
  // raised for a row nobody can see.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(visible.map((b) => b.id));
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visible]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const isSelected = (id) => selectedIds.has(id);

  const handleCreateForSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setCreateMessage('Select at least one booking.');
      return;
    }
    setCreateLoading(true);
    setCreateMessage(null);
    setCreateErrors([]);
    try {
      const res = await creditNoteService.createFromBookings(ids);
      const data = res.data;
      const created = data?.created || [];
      const errs = data?.errors || [];
      if (created.length > 0) {
        setCreateMessage(`${created.length} credit note(s) created (${created.map((c) => c.creditNoteNumber).join(', ')}). Sending emails…`);
        setSelectedIds(new Set());
        await load();
        const mail = await sendCreditNoteEmails(created);
        setCreateMessage(`${created.length} credit note(s) created. ${mail.sent.length} email(s) sent`
          + (mail.failed.length ? `, ${mail.failed.length} FAILED — see below.` : '.'));
        if (mail.failed.length) setCreateErrors(mail.failed);
      }
      if (errs.length > 0) setCreateErrors(errs);
    } catch (err) {
      setCreateErrors([err.response?.data?.message || err.message || 'Failed to create credit notes']);
    } finally {
      setCreateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Pending credit notes</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Pending credit notes</h1>
        <p className="confirmed-cn-intro">
          Bookings whose talent is confirmed and whose Gutschrift is still outstanding. Create the
          document once the job is done: tick several bookings of the same talent to put them on one
          PDF as multiple lines, or use the tick on a single row. Creating it emails the PDF to the
          talent — after that the document is fixed and a correction needs a Storno and a new
          number, so check the tasks first.
        </p>
        <label className="confirmed-cn-intro" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={onlyFinished}
            onChange={(e) => { setOnlyFinished(e.target.checked); clearSelection(); }}
          />
          <span>
            Only jobs that have ended
            {runningCount > 0 && (
              <span className="cell-muted"> — {runningCount} still running{onlyFinished ? ' (hidden)' : ''}</span>
            )}
          </span>
        </label>

        {/* Filters — combine freely; every dropdown only offers values present in the queue */}
        <div className="cn-filters">
          <MultiSelectFilter label="Talents" options={talentOptions} selected={filterTalents} onChange={setFilterTalents} />
          <MultiSelectFilter label="Agencies" options={agencyOptions} selected={filterAgencies} onChange={setFilterAgencies} />
          <MultiSelectFilter label="Clients" options={clientOptions} selected={filterClients} onChange={setFilterClients} />
          <MultiSelectFilter label="Users" options={userOptions} selected={filterUsers} onChange={setFilterUsers} />
          <MultiSelectFilter label="Week" options={weekOptions} selected={filterWeeks} onChange={setFilterWeeks} />
          <MultiSelectFilter label="Set" options={setOptions} selected={filterSets} onChange={setFilterSets} />
          <MultiSelectFilter label="Location" options={locationOptions} selected={filterLocations} onChange={setFilterLocations} />
          <MultiSelectFilter label="Function" options={functionOptions} selected={filterFunctions} onChange={setFilterFunctions} />
          <button type="button" className="button cn-filter-reset" onClick={resetFilters}>
            Reset filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <span className="cell-muted">
            {visible.length} of {bookings.length} booking{bookings.length !== 1 ? 's' : ''} shown
          </span>
        </div>

        <div className="confirmed-cn-actions">
          <button
            type="button"
            className="cn-btn cn-btn-primary"
            onClick={handleCreateForSelected}
            disabled={createLoading || selectedIds.size === 0}
            title="One credit note per talent; selected bookings from same talent become multiple lines on one PDF/XML"
          >
            {createLoading ? 'Creating…' : `Create credit note for selected (${selectedIds.size})`}
          </button>
          <button type="button" className="cn-btn cn-btn-secondary" onClick={clearSelection} disabled={selectedIds.size === 0}>
            Clear selection
          </button>
          <button type="button" className="cn-btn cn-btn-secondary" onClick={() => navigate('/admin/credit-notes')}>
            View all credit notes
          </button>
        </div>
        {createMessage && (
          <div className="confirmed-cn-message success">
            {createMessage}
          </div>
        )}
        {createErrors.length > 0 && (
          <div className="confirmed-cn-message error">
            <strong>Could not create credit note(s) — missing data:</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
              {createErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {error && (
          <div className="confirmed-cn-message error">
            {error}
          </div>
        )}
        <div className="content-wrapper content-wrapper-talent-list">
          <div className="list-tasks-header">
            <div style={{ flexBasis: '60px' }}>Select</div>
            <div style={{ flexBasis: '80px' }}>ID</div>
            <div style={{ flexBasis: '200px' }}>Title</div>
            <div style={{ flexBasis: '120px' }}>Start date</div>
            <div style={{ flexBasis: '120px' }}>End date</div>
            <div style={{ flexBasis: '150px' }}>Client</div>
            <div style={{ flexBasis: '120px' }}>Status</div>
            <div style={{ flexBasis: '150px' }}>Talent</div>
            <div style={{ flexBasis: '150px' }}>Agency</div>
            <div style={{ flexBasis: '80px' }}></div>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="confirmed-cn-empty">
            {bookings.length === 0
              ? 'No bookings are waiting for a credit note.'
              : activeFilterCount > 0
                ? 'No bookings match the current filters.'
                : `No finished jobs — all ${runningCount} booking(s) in the queue are still running.`}
          </div>
        ) : (
          visible.map((b) => (
            <div className="list-tasks-item" key={b.id} style={{ paddingLeft: '20px' }}>
              <div style={{ flexBasis: '60px' }}>
                {b.credit_note_generated ? (
                  '—'
                ) : (
                  <input
                    type="checkbox"
                    checked={isSelected(b.id)}
                    onChange={() => toggleSelection(b.id)}
                    aria-label={`Select booking ${b.id}`}
                  />
                )}
              </div>
              <div style={{ flexBasis: '80px' }}>{b.id}</div>
              <div style={{ flexBasis: '200px' }}>
                {/* Same string as the Lano task title: week, client, function, talent, dates, location */}
                <div title={lanoTitle(b)} style={{ wordBreak: 'break-all' }}>{lanoTitle(b)}</div>
                {/* The Set the filter works on; the booking's own free-text title is deliberately not shown */}
                <div className="cell-muted">{setDisplay(b)}</div>
              </div>
              <div style={{ flexBasis: '120px' }}>
                {formatDate(b.start_date)}
                {weekDisplay(b) && <div className="cell-muted">{weekDisplay(b)}</div>}
              </div>
              <div style={{ flexBasis: '120px' }}>
                {formatDate(b.end_date)}
                {!isFinished(b) && <span className="cell-muted"> running</span>}
              </div>
              <div style={{ flexBasis: '150px' }}>{clientName(b)}</div>
              <div style={{ flexBasis: '120px' }}>{statusDisplay(b)}</div>
              <div style={{ flexBasis: '150px' }}>{talentDisplay(b)}</div>
              <div style={{ flexBasis: '150px' }}>{agencyDisplay(b)}</div>
              <div style={{ flexBasis: '80px' }}>
                {b.credit_note_generated ? (
                  <span className="cell-muted">Created</span>
                ) : (
                  <a
                    className={`edit-button ${createLoading ? 'disabled' : ''}`}
                    onClick={() => !createLoading && handleCreateCreditNote(b)}
                    title="Create credit note"
                    style={{
                      opacity: createLoading ? 0.5 : 1,
                      cursor: createLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <img src={confirmIcon} alt="Create credit note" className="edit-container" />
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ConfirmedListCreditNotes;
